import { Injectable } from "@angular/core";
import {
    HttpClient,
    HttpErrorResponse,
    HttpResponse,
    HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { Branch } from "../model/branches";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { AuthService } from "../auth.service";
import { Labeler } from "../model/labeler";

import { PALETTE } from "../model/palette";

@Injectable({
    providedIn: "root",
})
export class BranchService implements Labeler {
    private apiUrl: string;
    public _currentBranches: BehaviorSubject<Branch[]>;
    public selectedBranch: BehaviorSubject<Branch | null>;
    constructor(
        private http: HttpClient,
        private env: EnvironmentHelper,
        private auth: AuthService
    ) {
        let empty: Array<Branch> = [];
        this._currentBranches = new BehaviorSubject(empty);
        this.apiUrl = env.apiBaseUrl + "branch/";
    }

    public branches(token: string): Observable<Messages> {
        return new Observable<Messages>((observer) => {
            this.http
                .get<any>(this.apiUrl, {
                    headers: { Authorization: "Bearer " + token },
                    observe: "response",
                })
                .subscribe(
                    (response) => {
                        this.receiveBranches(response, observer);
                    },
                    (error) => {
                        this.receiveBranches(error, observer);
                    }
                );
        });
    }
    private receiveBranches(
        response: HttpResponseBase,
        observer: Subscriber<Messages>
    ) {
        if (response.status === 200) {
            const data = (response as HttpResponse<Branch[]>)?.body;
            if (!data) {
                observer.next(Messages.from({ branches: ["branches.unknown"] }));
                return;
            }
            this._currentBranches.next(data);
            observer.next(Messages.EMPTY);
        } else if (response.status === 401) {
            observer.next(Messages.from({ account: ["login.not.authorized"] }));
        } else {
            observer.next(
                Messages.orElse(
                    (response as HttpErrorResponse).error,
                    "branches",
                    "branches.incomplete"
                )
            );
        }
    }

    public select(branch: Branch) {
        this.selectedBranch.next(branch);
    }

    public get branchesObservable(): Observable<Branch[]> {
        return this._currentBranches;
    }
    public get branchesData(): Branch[] {
        return this._currentBranches.value;
    }

    /**
     * A location by its abbreviation, or by its name where it has none: several
     * locations have no abbreviation, and "-" for each of them told them apart
     * from nothing (#173).
     */
    public labelForId(id: number): string {
        const found = this.branchesData?.find((branch) => branch.id === id);
        return found ? BranchService.shortName(found) : "-";
    }

    /** The abbreviation, or the name where there is none. */
    public static shortName(branch: Branch): string {
        return branch.abbreviation?.trim() || branch.name?.trim() || "-";
    }
    public makeLabel(id: number): Observable<string> {
        const observable = new BehaviorSubject("");

        if (this.branchesData?.length > 0) {
            const label = this.labelForId(id);
            observable.next(label);
        } else {
            this.branches(this.auth.token).subscribe((m) => {
                observable.next(this.labelForId(id));
            });
        }
        return observable;
    }
    public makeColor(id: number): Observable<string> {
        return new BehaviorSubject(PALETTE.ink);
    }
    /** The colour of the location category: zwembadblauw (#173). */
    public makeBackgroundColor(id: number): Observable<string> {
        return new BehaviorSubject(PALETTE.zwembadblauw);
    }

    public makeImportance(id: number): Observable<number> {
        return new BehaviorSubject(1);
    }
}
